import { useMemo } from 'react'
import { gql } from '@apollo/client'
import { useTranslations } from '@npco/utils-translations'
import {
  Box,
  NumberBadge,
  NumberBadgeVariant,
  TooltipBasic,
} from '@npco/zeller-design-system'
import { useCorporateCardWalkthroughStage } from 'features/CorporateCardWalkthrough/corporate-card-walkthrough-routing/hooks/useCorporateCardWalkthroughStage/useCorporateCardWalkthroughStage'

import { cardTransactionsMissingDetailsCountCellTranslations } from './CardTransactionsMissingDetailsCountCell.i18n'
import { CardTransactionsMissingDetailsCountCellContentDebitCardV2Fragment } from './CardTransactionsMissingDetailsCountCellContent.generated'
import { getOutstandingTransactionsCount } from './CardTransactionsMissingDetailsCountCellContent.utils'

interface CardTransactionsMissingDetailsCountCellContentProps {
  card?: CardTransactionsMissingDetailsCountCellContentDebitCardV2Fragment
  index: number
}
export const CardTransactionsMissingDetailsCountCellContent = ({
  card,
  index,
}: CardTransactionsMissingDetailsCountCellContentProps) => {
  const t = useTranslations(cardTransactionsMissingDetailsCountCellTranslations)
  const { stage } = useCorporateCardWalkthroughStage()

  const outstandingTransactionsCount = useMemo(() => {
    return getOutstandingTransactionsCount(
      stage,
      index,
      card?.outstandingTransactions ?? 0
    )
  }, [card?.outstandingTransactions, stage, index])

  if (outstandingTransactionsCount === 0) {
    return null
  }

  return (
    <TooltipBasic
      placement="top"
      renderTrigger={({ ref, handlers }) => (
        <span ref={ref} {...handlers} data-testid="card-transaction">
          <NumberBadge
            variant={NumberBadgeVariant.SoftAlert}
            text={`${
              outstandingTransactionsCount > 99
                ? t('outstandingTransactionsCountMax')
                : outstandingTransactionsCount
            }`}
          />
        </span>
      )}
      showArrow={false}
    >
      <Box textAlign="center" width="144px">
        {outstandingTransactionsCount === 1
          ? t('outstandingSingle')
          : t('outstandingPlural', {
              count: outstandingTransactionsCount.toLocaleString(),
            })}
      </Box>
    </TooltipBasic>
  )
}

CardTransactionsMissingDetailsCountCellContent.fragments = {
  DebitCardV2: gql`
    fragment CardTransactionsMissingDetailsCountCellContentDebitCardV2Fragment on DebitCardV2 {
      outstandingTransactions
    }
  `,
}
